<template>
    <cod-dialog
        :show.sync="showDialog"
        max-width="430px"
        :loading="loading"
    >
        <div slot="header">
            {{ $t('integrations.ota_sync.room_send') }}
        </div>

        <div slot="content" class="send-to-room">
            <template v-if="!loading">
                <v-alert
                    v-if="alert"
                    :type="alert.type"
                    text
                >
                    {{ alert.message }}
                </v-alert>

                <template v-if="loggedIn">
                    <v-autocomplete
                        v-model="form.id_reservations_rooms"
                        :items="rooms"
                        :label="$t('integrations.ota_sync.room_choose')"
                        item-text="room_name"
                        item-value="id_reservations_rooms"
                        clear-icon="fal fa-times"
                        clearable
                        auto-select-first
                        :error-messages="form.$errors.id_reservations_rooms"
                        @blur="form.validate('id_reservations_rooms')"
                        @input="delete form.$errors.id_reservations_rooms"
                    />
                </template>

                <template v-else>
                    <v-text-field
                        v-model.trim="loginForm.username"
                        :error-messages="loginForm.$errors.username"
                        :label="$t('general.username')"
                        autofocus
                        validate-on-blur
                        class="mt-5"
                        @blur="loginForm.validate('username')"
                        @input="delete loginForm.$errors.username"
                    />

                    <v-text-field
                        v-model="loginForm.password"
                        :error-messages="loginForm.$errors.password"
                        :append-icon="showPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showPassword ? 'text' : 'password'"
                        :label="$t('general.password')"
                        @blur="loginForm.validate('password')"
                        @input="delete loginForm.$errors.password"
                        @click:append="showPassword = !showPassword"
                        @keyup.enter.native="login"
                    />
                </template>
            </template>
        </div>

        <template slot="footer">
            <v-btn
                class="flex-1 mr-1"
                color="primary"
                outlined
                large
                :disabled="loading"
                @click="showDialog = false"
            >
                {{ $t('general.close') }}
            </v-btn>

            <v-btn
                class="flex-1 ml-1"
                color="primary"
                depressed
                large
                :disabled="loading || (loggedIn && !form.id_reservations_rooms)"
                @click="confirm"
            >
                {{ !loggedIn ? $t('general.login') : $t('general.confirm') }}
            </v-btn>
        </template>
    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import Order from '@/models/Order'
import Client from '@/models/Client'
import Printable from '@/utilities/Printable'
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators'

export default {
    components: {
        CodDialog
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            showDialog: false,
            form: this.createForm(),
            loginForm: this.createLoginForm(),
            showPassword: false,
            loggedIn: true,
            alert: null,
            loading: false,
            rooms: []
        }
    },

    validations: {
        form: {
            id_reservations_rooms: { required },
        }
    },

    watch: {
        show(val) {
            this.showDialog = val;

            if (val) {
                this.form = this.createForm()
                this.loginForm = this.createLoginForm()

                this.getOccupiedRooms()
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },
    },

    computed: {
        ...mapGetters(['curOrder', 'unpaidOrders']),
    },

    methods: {
        createForm() {
            return new Form(this, 'form', {
                id_reservations: null,
                id_reservations_rooms: null,
                order: null,
            })
        },

        createLoginForm() {
            return new Form(this, 'loginForm', {
                username: null,
                password: null,
            })
        },

        confirm() {
            if(!this.loggedIn) {
                this.login()
                return
            }

            this.sendToRoom()
        },

        async login() {
            if (!this.loginForm.validate()) return;

            this.loading = true

            const response = await ApiService.post('/integrations/ota-sync/login', this.loginForm);

            if(response.data.success) {
                this.loggedIn = true
                this.getOccupiedRooms()
                return
            }

            this.loggedIn = false
            this.alert = {
                type: 'error',
                message: this.getError(response.data.error)
            }

            this.loading = false
        },

        async getOccupiedRooms() {
            this.loading = true

            const response = await ApiService.get('/integrations/ota-sync/occupied-rooms');

            if(!response.data.success) {
                if(response.data.error === 'auth') {
                    this.loggedIn = false
                    this.alert = {
                        type: 'warning',
                        message: this.$t('integrations.ota_sync.login_needed')
                    }
                } else {
                    this.alert = {
                        type: 'error',
                        message: this.getError(response.data.error)
                    }
                }
            } else {
                this.alert = {
                    type: 'info',
                    message: this.$t('integrations.ota_sync.room_choose_message')
                }
                this.rooms = response.data.rooms
            }

            this.loading = false
        },

        async sendToRoom() {
            if (!this.form.validate()) return;

            const room = this.rooms.find(r => r.id_reservations_rooms === this.form.id_reservations_rooms)
            if(!room) return

            this.form.id_reservations = room.id_reservations

            let order = null
            if(!this.unpaidOrders.length) {
                order = this.curOrder.$duplicate()
                order.status = Order.STATUS_PAID
            } else {
                order = Order.createNew(this.user, this.curOrder.getTable())
                order.status = locale === 'me' ? Order.STATUS_SUMMARY : Order.STATUS_PAID
                order.hash = this.unpaidOrders[0].hash.slice(5) + this.unpaidOrders[0].hash.slice(0, 5)
                order.note = this.curOrder.note
                order.client_id = this.curOrder.client_id

                this.unpaidOrders.forEach(o => order.addReference(o, 'summary'))

                order.items = locale === 'me'
                    ? this.unpaidOrders.map(o => OrderItem.createForOrder(order, o))
                    : OrderItem.merge(this.unpaidOrders.flatMap(o => o.items), order)
            }

            order.user_id = this.user.id
            order.last_activity_at = moment().utc().format(Order.DATE_FORMAT)
            order.number = null
            order.register_type = Order.REGISTER_TYPE_EXTERNAL
            order.taxes = Order.calculateTaxes(order)
            order.setFlag('otaSync', room.room_name, this.form.id_reservations, this.form.id_reservations_rooms);

            this.form.order = order

            this.loading = true

            const response = await ApiService.post('/integrations/ota-sync/send-order', this.form);

            this.loading = false

            if(!response.data.success) {
                this.alert = {
                    type: 'error',
                    message: this.getError(response.data.error)
                }
                return
            }

            this.$snackbar(this.$t('integrations.ota_sync.room_send_success'), 'success', { timeout: 3000 })

            this.$emit('save');
            this.showDialog = false;

            // Print Slip
            const receiptPrinter = this.$printService.getAssignedPrinter('receipt');
            const useReceiptPrinter = !!receiptPrinter && this.device.platform !== 'web';
            const text = useReceiptPrinter ? this.getPrintable(order).toText(receiptPrinter.charsPerLine).join('\n') : null;

            const printSlip = () => this.$printService.printCustom(text, this.getPrintable(order).toHtml().join(''), null, { choosePrinter: !useReceiptPrinter })

            printSlip()
            setTimeout(() => printSlip(), 3000)
        },

        getError(error) {
            if(!error) return this.$t('general.action_failed');

            return this.$t(`integrations.ota_sync.error.${error}`) !== `integrations.ota_sync.error.${error}`
                ? this.$t(`integrations.ota_sync.error.${error}`)
                : this.$t('general.action_failed');
        },

        getPrintable(order) {
            const pr = new Printable()

            let client = order.client || Client.find(order.client_id)

            const venue = this.tenant?.venue
            const receiptHeaderText = venue?.receipt_note?.split('|')[1] || '';

            // Receipt Header
            pr.l('D', '', { delimiter: '=' })
            pr.l('L', venue.company_name, { align: 'C' })
            pr.l('L', venue.name, { align: 'C' })

            if(venue.tax_id_number && venue.tax_id_number.length !== 13) {
                pr.l('L', `${this.$t('general.tax_id_number')}: ${venue.tax_id_number}`, { align: 'C' })
            }

            pr.l('L', venue.address, { align: 'C' })

            if(receiptHeaderText) {
                receiptHeaderText.split('\n').forEach(l => pr.l('L', l, { align: 'C' }))
            }

            pr.l('D', '', { delimiter: '=' })

            // Client
            if(client?.name) {
                pr.l('L', `${this.$tc('general.buyer', 1)}: ${client.name}`)
                if(client.address) {
                    pr.l('L', `${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`)
                }
                let taxIdNumberPrinted = false;
                if((client.country && client.country !== 'MNE') && client.id_number) {
                    const idType = this.$t(`general.${client.id_type}`)
                    pr.l('L', `${idType}: ${client.id_number}`)
                    taxIdNumberPrinted = client.id_type === 'tax_id_number';
                }
                if(!taxIdNumberPrinted && client.tax_id_number) {
                    pr.l('L', `${this.$t('general.tax_id_number')}: ${client.tax_id_number}`)
                }
                if(client.tax_id_card_number) {
                    pr.l('L', `${this.$t('general.tax_id_card_number')}: ${client.tax_id_card_number}`)
                }
                pr.l('D', '', { delimiter: '-' })
            }

            // Receipt Subheader
            pr.l('L', `${this.$t('general.time')}: ${order.getLastActivityAt().format('DD.MM.YYYY. HH:mm:ss')}`)
            pr.l('L', `${this.$t('general.operator')}: ${order.getUser().name}`)
            pr.l('L', `${this.$t('general.operator_code')}: ${order.getUser().hash}`)

            if(order.getFlag('otaSync').length) {
                pr.l('L', `${this.$t('integrations.ota_sync.room_number')}: ${order.getFlag('otaSync')[0]}`)
            }

            if(order.getTable()) {
                pr.l('L', `${this.$t('general.table')}: ${order.getTable().name}`)
            }

            // Product Items
            let productItems = order.isSummary()
                ? OrderItem.merge(order.paysForOrders().flatMap(order => order.items), order)
                : order.items

            if(productItems.length) {
                pr.l('D', '', { delimiter: '-' })
                pr.l('S', `${this.$tc('general.item', 1)} (${this.$t('tax_rates.rate')})`, this.$t('general.price'))
                pr.l('D', '', { delimiter: '-' })
            }

            productItems.forEach(item => {
                const count = item.getCount()
                const name = item.name
                const unitPrice = item.getUnitPriceAfterTax().toFormat('0.00')
                const price = item.getPrice().toFormat('0.00')
                const discountPercent = item.getDiscountPercent()
                const discountAmount = item.getDiscountAmount().multiply(-1).toFormat('0.00')
                const toPay = item.getToPay().toFormat('0.00')
                pr.l('L', `${name} (${item.getTaxRate()}%)`)
                pr.l('S', `   ${count} x ${unitPrice}`, price)

                if(discountPercent > 0) {
                    pr.l('S', `   ${this.$t('general.discount')} (${discountPercent}%)`, discountAmount)
                    pr.l('S', `   ${this.$t('general.with_discount')}`, toPay)
                }
            });

            pr.l('D', '', { delimiter: '-' })

            // Price and Discount
            pr.l('S', this.$t('general.total'), order.getPrice().toFormat('0.00'))
            pr.l('S', this.$t('general.discount'), order.getDiscountAmount().toFormat('0.00'))
            pr.l('D', '', { delimiter: '-' })
            pr.l('S', this.$t('general.to_pay').toUpperCase(), order.getToPay().toFormat('0.00'), { scale: '2,2' })

            let note = order.note || '';
            if(!venue.taxes_enabled) {
                note = '* ' + this.$t('order.exempt_from_tax_comment') + '\n\n' + note;
            }

            if(note) {
                pr.l('D', '', { delimiter: '-' })
                note.split('\n').forEach(l => pr.l('L', l))
            }

            pr.l('D', '', { delimiter: '-' })
            pr.l('L', this.$t('integrations.ota_sync.receipt_note'))

            return pr
        }
    }
};
</script>

<style lang="scss" scoped>
.send-to-room {
    padding: 16px;
    min-height: 270px;
}
</style>
